import { lazy, Suspense, useEffect, useId, useState } from 'react';

import { Button } from '@/components/ui/Button';
import { Modal } from '@/components/ui/Modal';
import { OverflowMenu } from '@/components/ui/OverflowMenu';
import { ErrorState, LoadingState } from '@/components/ui/StateBlock';
import { formatDateTime } from '@/lib/format';
import { describeError } from '@/lib/http';

import {
  fetchChatGptResult,
  type ChatGptPromptId,
  type ChatGptResult,
  type ChatGptResultSummary,
} from '../api';
import { ANALYSIS_PROMPTS } from '../buildTranscriptExport';
import {
  copyHtmlAndText,
  isEmptyRichHtml,
  sanitizeChatGptHtmlClient,
} from '../chatgptHtml';
import {
  useChatGptResultsQuery,
  useCreateChatGptResultMutation,
  useDeleteChatGptResultMutation,
  useUpdateChatGptResultMutation,
} from '../queries';

const ChatGptResultEditor = lazy(async () => {
  const module = await import('./ChatGptResultEditor');
  return { default: module.ChatGptResultEditor };
});

export type InitialChatGptPrompt = {
  id: ChatGptPromptId;
  label: string;
};

export type ChatGptCreateRequest = {
  prompt: InitialChatGptPrompt;
  /** Меняйте значение, чтобы повторно открыть форму с тем же промтом. */
  nonce: number;
};

export type ChatGptViewRequest = {
  resultId: string;
  nonce: number;
};

type Props = {
  transcriptionId: string;
  enabled: boolean;
  currentUserId: string | null | undefined;
  isAdmin: boolean;
  createRequest?: ChatGptCreateRequest | null;
  onCreateRequestConsumed?: () => void;
  viewRequest?: ChatGptViewRequest | null;
  onViewRequestConsumed?: () => void;
  /** Открывает полный маршрут с выбора промта (не пустую форму). */
  onAddAnother?: () => void;
  /** Устаревший bridge — оставлен для совместимости; предпочтите createRequest. */
  initialPrompt?: InitialChatGptPrompt | null;
  onInitialPromptConsumed?: () => void;
};

type EditorMode = 'create' | 'edit';

const OTHER_PROMPT: InitialChatGptPrompt = { id: 'other', label: 'Другой результат' };

const PROMPT_OPTIONS: InitialChatGptPrompt[] = [
  ...ANALYSIS_PROMPTS.map((prompt) => ({ id: prompt.id as ChatGptPromptId, label: prompt.label })),
  { id: 'custom', label: 'Свой промт' },
  OTHER_PROMPT,
];

function canManageResult(
  result: Pick<ChatGptResultSummary, 'createdById'>,
  currentUserId: string | null | undefined,
  isAdmin: boolean,
): boolean {
  return (
    isAdmin ||
    (currentUserId !== null && currentUserId !== undefined && result.createdById === currentUserId)
  );
}

export function ChatGptResultsSection({
  transcriptionId,
  enabled,
  currentUserId,
  isAdmin,
  createRequest = null,
  onCreateRequestConsumed,
  viewRequest = null,
  onViewRequestConsumed,
  onAddAnother,
  initialPrompt = null,
  onInitialPromptConsumed,
}: Props) {
  const titleId = useId();
  const listQuery = useChatGptResultsQuery(transcriptionId, enabled);
  const createMutation = useCreateChatGptResultMutation(transcriptionId);
  const updateMutation = useUpdateChatGptResultMutation(transcriptionId);
  const deleteMutation = useDeleteChatGptResultMutation(transcriptionId);

  const [editorOpen, setEditorOpen] = useState(false);
  const [editorMode, setEditorMode] = useState<EditorMode>('create');
  const [editing, setEditing] = useState<ChatGptResult | null>(null);
  const [viewing, setViewing] = useState<ChatGptResult | null>(null);
  const [contentLoading, setContentLoading] = useState(false);
  const [promptId, setPromptId] = useState<ChatGptPromptId>('other');
  const [promptLabel, setPromptLabel] = useState(OTHER_PROMPT.label);
  const [title, setTitle] = useState('');
  const [html, setHtml] = useState('<p></p>');
  const [formError, setFormError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const openCreate = (prompt: InitialChatGptPrompt = OTHER_PROMPT) => {
    setEditorMode('create');
    setEditing(null);
    setPromptId(prompt.id);
    setPromptLabel(prompt.label);
    setTitle(prompt.label);
    setHtml('<p></p>');
    setFormError(null);
    setEditorOpen(true);
  };

  useEffect(() => {
    if (createRequest === null) {
      return;
    }
    openCreate(createRequest.prompt);
    onCreateRequestConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- открываем по nonce
  }, [createRequest]);

  useEffect(() => {
    if (initialPrompt === null) {
      return;
    }
    openCreate(initialPrompt);
    onInitialPromptConsumed?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- callback нестабилен
  }, [initialPrompt]);

  useEffect(() => {
    if (viewRequest === null) {
      return;
    }
    const summary = listQuery.data?.items.find((item) => item.id === viewRequest.resultId);
    if (summary === undefined) {
      onViewRequestConsumed?.();
      return;
    }
    void openView(summary).finally(() => {
      onViewRequestConsumed?.();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- открываем по nonce
  }, [viewRequest, listQuery.data?.items]);

  useEffect(() => {
    if (notice === null) {
      return;
    }
    const timer = window.setTimeout(() => setNotice(null), 2200);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const loadFullResult = async (resultId: string): Promise<ChatGptResult> => {
    setContentLoading(true);
    try {
      return await fetchChatGptResult(transcriptionId, resultId);
    } finally {
      setContentLoading(false);
    }
  };

  const openEdit = async (summary: ChatGptResultSummary) => {
    try {
      const full = await loadFullResult(summary.id);
      setEditorMode('edit');
      setEditing(full);
      setPromptId(full.promptId);
      setPromptLabel(full.promptLabel);
      setTitle(full.title);
      setHtml(full.contentHtml);
      setFormError(null);
      setEditorOpen(true);
    } catch (cause) {
      setNotice(describeError(cause));
    }
  };

  const openView = async (summary: ChatGptResultSummary) => {
    try {
      const full = await loadFullResult(summary.id);
      setViewing(full);
    } catch (cause) {
      setNotice(describeError(cause));
    }
  };

  const closeEditor = () => {
    setEditorOpen(false);
    setEditing(null);
    setFormError(null);
  };

  const onPromptSelect = (nextId: ChatGptPromptId) => {
    const option = PROMPT_OPTIONS.find((item) => item.id === nextId) ?? OTHER_PROMPT;
    setPromptId(option.id);
    setPromptLabel(option.label);
    if (title.trim() === '' || PROMPT_OPTIONS.some((item) => item.label === title)) {
      setTitle(option.label);
    }
  };

  const onSave = async () => {
    setFormError(null);
    const safeHtml = sanitizeChatGptHtmlClient(html);
    if (isEmptyRichHtml(safeHtml)) {
      setFormError('Добавьте текст результата');
      return;
    }
    const trimmedTitle = title.trim();
    if (trimmedTitle === '') {
      setFormError('Укажите название');
      return;
    }
    const body = {
      promptId,
      promptLabel: promptLabel.trim() || OTHER_PROMPT.label,
      title: trimmedTitle,
      contentHtml: safeHtml,
    };
    try {
      if (editorMode === 'create') {
        await createMutation.mutateAsync(body);
        closeEditor();
        setNotice('Результат прикреплён');
      } else if (editing !== null) {
        await updateMutation.mutateAsync({ resultId: editing.id, body });
        closeEditor();
        setNotice('Результат сохранён');
      }
    } catch (cause) {
      setFormError(describeError(cause));
    }
  };

  const onCopy = async (summary: ChatGptResultSummary) => {
    try {
      const full = await loadFullResult(summary.id);
      await copyHtmlAndText(full.contentHtml, full.contentText);
      setNotice('Результат скопирован с форматированием');
    } catch (cause) {
      setNotice(cause instanceof Error ? cause.message : 'Не удалось скопировать');
    }
  };

  const onDelete = async (result: ChatGptResultSummary) => {
    const ok = window.confirm(`Удалить результат «${result.title}»?`);
    if (!ok) {
      return;
    }
    try {
      await deleteMutation.mutateAsync(result.id);
      setNotice('Результат удалён');
      if (viewing?.id === result.id) {
        setViewing(null);
      }
    } catch (cause) {
      setNotice(describeError(cause));
    }
  };

  const items = listQuery.data?.items ?? [];
  const saving = createMutation.isPending || updateMutation.isPending;
  const showAddAnother = items.length > 0 && onAddAnother !== undefined;

  return (
    <section className="chatgpt-results" aria-labelledby={titleId}>
      <div className="chatgpt-results__header">
        <h2 className="chatgpt-results__title" id={titleId}>
          Результаты ChatGPT
        </h2>
        {showAddAnother ? (
          <Button
            type="button"
            variant="secondary"
            size="sm"
            disabled={!enabled}
            onClick={onAddAnother}
          >
            + Добавить ещё результат
          </Button>
        ) : null}
      </div>

      {notice !== null ? (
        <p className="chatgpt-results__notice" role="status">
          {notice}
        </p>
      ) : null}

      {listQuery.isPending ? <LoadingState title="Загрузка результатов…" /> : null}
      {listQuery.isError ? (
        <ErrorState
          title="Не удалось загрузить результаты"
          description={describeError(listQuery.error)}
        />
      ) : null}
      {contentLoading ? (
        <p className="chatgpt-results__notice" role="status">
          Загрузка содержимого…
        </p>
      ) : null}

      {!listQuery.isPending && !listQuery.isError && items.length === 0 ? (
        <div className="chatgpt-results__empty">
          <p className="chatgpt-results__empty-title">Пока нет сохранённых результатов</p>
          <p className="chatgpt-results__empty-text">
            Подготовьте анализ через блок «Следующий шаг», получите ответ в ChatGPT и прикрепите его
            здесь.
          </p>
        </div>
      ) : null}

      {items.length > 0 ? (
        <ul className="chatgpt-results__list">
          {items.map((result) => {
            const editable = canManageResult(result, currentUserId, isAdmin);
            const updated =
              result.updatedAt !== result.createdAt
                ? ` · обновлён ${formatDateTime(result.updatedAt)}`
                : '';
            const menuItems = [
              {
                id: 'copy',
                label: 'Скопировать',
                onSelect: () => {
                  void onCopy(result);
                },
              },
              ...(editable
                ? [
                    {
                      id: 'edit',
                      label: 'Изменить',
                      onSelect: () => {
                        void openEdit(result);
                      },
                    },
                    {
                      id: 'delete',
                      label: 'Удалить',
                      danger: true,
                      onSelect: () => {
                        void onDelete(result);
                      },
                    },
                  ]
                : []),
            ];
            return (
              <li key={result.id} className="chatgpt-results__item">
                <button
                  type="button"
                  className="chatgpt-results__card"
                  onClick={() => {
                    void openView(result);
                  }}
                >
                  <div className="chatgpt-results__card-main">
                    <div className="chatgpt-results__item-meta">{result.promptLabel}</div>
                    <div className="chatgpt-results__item-title">{result.title}</div>
                    <div className="chatgpt-results__item-sub">
                      {result.createdBy.displayName} · {formatDateTime(result.createdAt)}
                      {updated}
                    </div>
                  </div>
                  <span className="chatgpt-results__card-arrow" aria-hidden="true">
                    →
                  </span>
                </button>
                <div
                  className="chatgpt-results__item-menu"
                  onClick={(event) => event.stopPropagation()}
                  onKeyDown={(event) => event.stopPropagation()}
                >
                  <OverflowMenu label={`Действия результата «${result.title}»`} items={menuItems} />
                </div>
              </li>
            );
          })}
        </ul>
      ) : null}

      <Modal
        open={editorOpen}
        onClose={closeEditor}
        title={editorMode === 'create' ? 'Прикрепить результат ChatGPT' : 'Изменить результат'}
        subtitle="Вставьте ответ из ChatGPT — форматирование сохранится"
        width="920px"
      >
        <div className="chatgpt-result-form">
          <label className="chatgpt-result-form__field">
            <span>Промт</span>
            <select
              value={promptId}
              disabled={saving}
              onChange={(event) => onPromptSelect(event.target.value as ChatGptPromptId)}
            >
              {PROMPT_OPTIONS.map((option) => (
                <option key={option.id} value={option.id}>
                  {option.label}
                </option>
              ))}
            </select>
          </label>
          <label className="chatgpt-result-form__field">
            <span>Название</span>
            <input
              type="text"
              value={title}
              maxLength={500}
              disabled={saving}
              onChange={(event) => setTitle(event.target.value)}
            />
          </label>
          <Suspense fallback={<LoadingState title="Загрузка редактора…" />}>
            <ChatGptResultEditor valueHtml={html} onChange={setHtml} disabled={saving} />
          </Suspense>
          {formError !== null ? (
            <p className="chatgpt-result-form__error" role="alert">
              {formError}
            </p>
          ) : null}
          <div className="chatgpt-result-form__actions">
            <Button
              type="button"
              variant="primary"
              disabled={saving}
              onClick={() => {
                void onSave();
              }}
            >
              {saving ? 'Сохранение…' : 'Сохранить'}
            </Button>
            <Button type="button" variant="secondary" disabled={saving} onClick={closeEditor}>
              Отмена
            </Button>
          </div>
        </div>
      </Modal>

      <Modal
        open={viewing !== null}
        onClose={() => setViewing(null)}
        title={viewing?.title ?? 'Результат'}
        {...(viewing !== null ? { subtitle: viewing.promptLabel } : {})}
        width="920px"
      >
        {viewing !== null ? (
          <div className="chatgpt-result-view">
            <div
              className="chatgpt-result-view__html"
              // HTML уже санитизирован на сервере; дополнительно чистим DOMPurify.
              dangerouslySetInnerHTML={{ __html: sanitizeChatGptHtmlClient(viewing.contentHtml) }}
            />
            <div className="chatgpt-result-view__actions">
              <Button
                type="button"
                variant="secondary"
                onClick={() => {
                  void copyHtmlAndText(viewing.contentHtml, viewing.contentText)
                    .then(() => setNotice('Результат скопирован с форматированием'))
                    .catch((cause: unknown) => {
                      setNotice(cause instanceof Error ? cause.message : 'Не удалось скопировать');
                    });
                }}
              >
                Копировать
              </Button>
              {canManageResult(viewing, currentUserId, isAdmin) ? (
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => {
                    setEditing(viewing);
                    setEditorMode('edit');
                    setPromptId(viewing.promptId);
                    setPromptLabel(viewing.promptLabel);
                    setTitle(viewing.title);
                    setHtml(viewing.contentHtml);
                    setFormError(null);
                    setEditorOpen(true);
                    setViewing(null);
                  }}
                >
                  Изменить
                </Button>
              ) : null}
            </div>
          </div>
        ) : null}
      </Modal>
    </section>
  );
}
